import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'

const asArray = (value) => (Array.isArray(value) ? value : [])

const SOCIAL_IMAGES = [
  { src: '/img/social/1.webp', altKey: 'slidingScale.images.alt1' },
  { src: '/img/social/2.webp', altKey: 'slidingScale.images.alt2' },
  { src: '/img/social/3.webp', altKey: 'slidingScale.images.alt3' },
]

const SlidingScale = () => {
  const t = useTranslation()
  const { isLoading, language } = useLanguage()
  const location = useLocation()

  const contactPath = language === 'pt' ? '/pt/contact' : '/contact'
  const whoItems = asArray(t('slidingScale.whoFor.items'))

  usePageSeo({
    enabled: !isLoading,
    title: `${t('slidingScale.meta.title')} | VIVA Psychotherapy`,
    description: t('slidingScale.meta.description'),
    pathname: location.pathname
  })

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading…</div>
      </div>
    )
  }

  const [img1, img2, img3] = SOCIAL_IMAGES

  return (
    <div className="min-h-screen bg-primary-offWhite">
      {/* Hero + photo collage */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-darkGreen via-primary-darkGreen to-primary-darkGreen/90 text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
          aria-hidden
        />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7 text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl lg:text-[2.35rem] font-bold mb-6 leading-tight tracking-tight">
                {t('slidingScale.hero.title')}
              </h1>
              <p className="text-lg sm:text-xl text-white/90 leading-relaxed mb-5">{t('slidingScale.hero.intro')}</p>
              <p className="text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl lg:max-w-none mx-auto lg:mx-0">
                {t('slidingScale.hero.announcement')}
              </p>
            </div>

            <div className="lg:col-span-5 max-w-md mx-auto lg:max-w-none w-full">
              <div className="space-y-4">
                <figure className="rounded-2xl overflow-hidden shadow-2xl shadow-black/25 ring-2 ring-white/20 aspect-[4/3] bg-white/10">
                  <img
                    src={img1.src}
                    alt={t(img1.altKey)}
                    className="w-full h-full object-cover"
                    width={800}
                    height={600}
                    decoding="async"
                  />
                </figure>
                <div className="grid grid-cols-2 gap-4">
                  <figure className="rounded-xl overflow-hidden shadow-lg shadow-black/20 ring-1 ring-white/15 aspect-square bg-white/10">
                    <img
                      src={img2.src}
                      alt={t(img2.altKey)}
                      className="w-full h-full object-cover"
                      width={400}
                      height={400}
                      loading="lazy"
                      decoding="async"
                    />
                  </figure>
                  <figure className="rounded-xl overflow-hidden shadow-lg shadow-black/20 ring-1 ring-white/15 aspect-square bg-white/10">
                    <img
                      src={img3.src}
                      alt={t(img3.altKey)}
                      className="w-full h-full object-cover"
                      width={400}
                      height={400}
                      loading="lazy"
                      decoding="async"
                    />
                  </figure>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <article className="relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 space-y-16 lg:space-y-20">
          {/* What is */}
          <section className="max-w-3xl mx-auto text-center lg:text-left lg:mx-0">
            <h2 className="text-2xl sm:text-3xl font-bold text-primary-darkGreen mb-5">
              {t('slidingScale.whatIs.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg">{t('slidingScale.whatIs.body')}</p>
          </section>

          {/* Highlights — three cards */}
          <section aria-label={t('slidingScale.highlights.sectionAria')}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              <div className="rounded-2xl border border-primary-darkGreen/12 bg-white p-6 lg:p-7 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h3 className="text-lg font-bold text-primary-darkGreen mb-2">{t('slidingScale.highlights.rateLabel')}</h3>
                <p className="text-gray-800 text-lg font-semibold leading-snug">{t('slidingScale.highlights.rateValue')}</p>
              </div>
              <div className="rounded-2xl border border-primary-darkGreen/12 bg-white p-6 lg:p-7 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h3 className="text-lg font-bold text-primary-darkGreen mb-2">{t('slidingScale.highlights.qualityTitle')}</h3>
                <p className="text-gray-700 leading-relaxed">{t('slidingScale.highlights.qualityBody')}</p>
              </div>
              <div className="rounded-2xl border border-primary-darkGreen/12 bg-white p-6 lg:p-7 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h3 className="text-lg font-bold text-primary-darkGreen mb-2">{t('slidingScale.highlights.bilingualTitle')}</h3>
                <p className="text-gray-700 leading-relaxed">{t('slidingScale.highlights.bilingualBody')}</p>
              </div>
            </div>
          </section>

          {/* Supervision | Who for — side by side at lg+; single-column flow inside each */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-14 xl:gap-16 items-start">
            <section>
              <h2 className="text-2xl sm:text-3xl font-bold text-primary-darkGreen mb-5 lg:mb-6">
                {t('slidingScale.supervision.title')}
              </h2>
              <p className="text-gray-700 leading-relaxed text-lg mb-5">{t('slidingScale.supervision.body1')}</p>
              <p className="text-gray-700 leading-relaxed text-lg">{t('slidingScale.supervision.body2')}</p>
            </section>

            <section>
              <h2 className="text-2xl sm:text-3xl font-bold text-primary-darkGreen mb-4 lg:mb-5">
                {t('slidingScale.whoFor.title')}
              </h2>
              <p className="text-gray-700 leading-relaxed text-lg mb-5">{t('slidingScale.whoFor.intro')}</p>
              <ul className="space-y-3 text-gray-700 leading-relaxed text-lg">
                {whoItems.map((item, index) => (
                  <li key={index} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-darkGreen" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {/* Belief */}
          <blockquote className="max-w-4xl mx-auto rounded-lg border border-primary-200 bg-green-50 px-6 py-8 shadow-sm sm:px-10 sm:py-10">
            <p className="text-gray-800 font-medium text-lg sm:text-xl leading-relaxed text-center">
              {t('slidingScale.belief')}
            </p>
          </blockquote>

          {/* Next step — contact form CTA at end */}
          <section className="mx-auto max-w-2xl text-center">
            <h2 className="mb-4 text-2xl font-bold text-primary-darkGreen sm:text-3xl">
              {t('slidingScale.nextStep.title')}
            </h2>
            <p className="text-lg leading-relaxed text-gray-700">{t('slidingScale.nextStep.body')}</p>
            <p className="mt-6 text-lg leading-relaxed text-gray-700">
              {t('slidingScale.nextStep.formCtaBefore')}{' '}
              <Link to={contactPath} className="font-semibold text-primary-darkGreen underline hover:no-underline">
                {t('slidingScale.nextStep.formLinkText')}
              </Link>{' '}
              {t('slidingScale.nextStep.formCtaAfter')}
            </p>
          </section>
        </div>
      </article>
    </div>
  )
}

export default SlidingScale
